import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { Box, Grid, Paper, Typography } from "@mui/material";
import { useParams } from "react-router-dom";
import LoaderContainer from "../../components/LoaderContainer";
import axios from "axios";
import { SERVER_BASE_ADDRESS } from "../../globals/const";
import ASQILogo from "../../assets/logo/ASQI-logo.png";
import ASQILogoBlue from "../../assets/logo/ASQI-logo-B4.png";

// Component for the invoice header
const InvoiceHeader = ({ invoiceData }) => (
  <>
    <Box sx={{ ml: "-50px" }}>
      <img
        src={ASQILogo}
        alt="ASQI Logo"
        style={{
          height: "120px",
          opacity: 0.9,
        }}
      />
    </Box>

    <Box>
      <Typography align="center" sx={{ fontWeight: "bold", color: "#000" }}>
        Tax Invoice
      </Typography>
      <Typography align="center" sx={{ fontWeight: "bold", color: "#000" }}>
        Original for Recipient
      </Typography>
    </Box>

    <Grid container mt={2} spacing={2} sx={{ mb: 4 }}>
      <Grid item xs={6}>
        <Typography>Invoice No: {invoiceData?.invoice_number}</Typography>
      </Grid>
      <Grid item xs={6} sx={{ textAlign: "right" }}>
        <Typography>Date: {invoiceData?.date_of_payment}</Typography>
      </Grid>
    </Grid>

    <Grid container mt={2} sx={{ mb: 4 }}>
      <Grid item xs={12}>
        <Typography>{invoiceData?.name}</Typography>
      </Grid>
      <Grid item xs={12}>
        <Typography>{invoiceData?.address}</Typography>
      </Grid>
    </Grid>
  </>
);

// Component for the invoice table
const InvoiceTable = ({ invoiceData }) => (
  <Box sx={{ mb: 4 }}>
    <Paper elevation={0}>
      <Grid
        container
        sx={{
          borderBottom: "2px solid #000",
          borderTop: "2px solid #000",
          px: 1,
          py: 0,
        }}
      >
        <Grid item xs={8} sx={{ fontWeight: "bold" }}>
          <Typography variant="body1" sx={{ fontWeight: "bold" }}>
            Particulars
          </Typography>
        </Grid>
        <Grid item xs={4} sx={{ textAlign: "right" }}>
          <Typography variant="body1" sx={{ fontWeight: "bold" }}>
            Amount (in INR)
          </Typography>
        </Grid>
      </Grid>

      <Grid container>
        <Grid item xs={8} sx={{ p: 1 }}>
          <Typography>Sale of EBC</Typography>
        </Grid>
        <Grid item xs={4} sx={{ p: 1, textAlign: "right" }}>
          <Typography>{invoiceData?.amount_without_gst}</Typography>
        </Grid>
      </Grid>

      <Grid
        container
        mt={3}
        sx={{
          borderTop: "2px solid #000",
        }}
      >
        <Grid item xs={8} sx={{ px: 1, py: 0, textAlign: "center" }}>
          <Typography>Amount</Typography>
        </Grid>
        <Grid item xs={4} sx={{ px: 1, py: 0, textAlign: "right" }}>
          <Typography sx={{ fontWeight: "bold" }}>
            {invoiceData?.amount_without_gst}
          </Typography>
        </Grid>
      </Grid>

      <Grid
        container
        sx={{
          borderBottom: "2px solid #000",
        }}
      >
        <Grid item xs={8} sx={{ textAlign: "center", px: 1, py: 0 }}>
          <Typography>GST @5%</Typography>
        </Grid>
        <Grid item xs={4} sx={{ px: 1, py: 0, textAlign: "right" }}>
          <Typography>{invoiceData?.gst_amount}</Typography>
        </Grid>
      </Grid>

      <Grid
        container
        sx={{
          borderBottom: "2px solid #000",
        }}
      >
        <Grid item xs={8} sx={{ py: 0, px: 1, fontWeight: "bold" }}>
          <Typography variant="body1" sx={{ fontWeight: "bold" }}>
            Gross Amount
          </Typography>
        </Grid>
        <Grid item xs={4} sx={{ py: 0, px: 1, textAlign: "right" }}>
          <Typography variant="body1" sx={{ fontWeight: "bold" }}>
            {invoiceData?.total_amount || "15,000"}
          </Typography>
        </Grid>
      </Grid>
    </Paper>

    <Box sx={{ mt: 2, mb: 4, py: 0, px: 1 }}>
      <Typography variant="body1" sx={{ fontWeight: "bold" }}>
        Amount in words : {invoiceData?.amount_in_words}
      </Typography>
    </Box>
  </Box>
);

// Component for the invoice footer info
const InvoiceFooterInfo = ({ invoiceData }) => (
  <>
    <Box sx={{ mt: 6, textAlign: "right" }}>
      <Box
        sx={{
          borderTop: "2px solid #000",
          display: "inline-block",
          width: "200px",
        }}
      >
        <Typography sx={{ mt: 1, textAlign: "center", fontWeight: "bold" }}>
          Authorised Signatory
        </Typography>
      </Box>
    </Box>
    <Box
      sx={{
        mt: 4,
        borderTop: "2px solid #000",
        borderBottom: "2px solid #000",
        py: 0,
        px: 1,
      }}
    >
      <Typography>PAN NO : AAECE2320A</Typography>
      <Typography>
        GST Registration No. : {invoiceData?.gst_registration_number}
      </Typography>
    </Box>
    <Grid container mt={2} sx={{ py: 0, px: 1 }}>
      <Grid item xs={12}>
        <Typography sx={{ fontWeight: "bold", fontSize: 14 }}>
          Terms & Conditions :
        </Typography>
      </Grid>{" "}
      <Grid item xs={12}>
        {invoiceData?.payment_mode === "CASH" ? (
          // Cash Payment Terms & Conditions
          <>
            <Typography fontSize={14}>
              a) This Bill is payable on receipt by Cash in favour of ASQI
              Technologies Private Limited
            </Typography>
            <Typography fontSize={14}>
              b) In case of any queries regarding the Invoice, please contact us
              on connect@asqi.in
            </Typography>
          </>
        ) : (
          // Digital Payment Terms & Conditions
          <>
            <Typography fontSize={14}>
              a) This Bill is payable on receipt by Wire transfer in favour of
              ASQI Investment Managers Private Limited
            </Typography>
            <Typography fontSize={14}>
              b) Please make payment immediately on the receipt of this invoice.
            </Typography>
            <Typography fontSize={14}>
              c) In case of any queries regarding the Invoice, please contact us
              on +91-22-61810000
            </Typography>
          </>
        )}
      </Grid>
    </Grid>{" "}
    <Grid container mt={2} sx={{ mb: 4, py: 0, px: 1 }}>
      <Grid item xs={12}>
        <Typography sx={{ fontWeight: "bold", fontSize: 14 }}>
          Bank Details :
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <Typography fontSize={14}>Bank Name : HDFC Bank Ltd</Typography>
        <Typography fontSize={14}>
          Address : Ground Floor, Unit No. 001A, Hallmark Business Plaza,
          Bandra(E), Mumbai - 400051
        </Typography>
        <Typography fontSize={14}>Account No : 08350350000999</Typography>
        <Typography fontSize={14}>RTGS / NEFT IFSC : HDFC0000835</Typography>
      </Grid>
    </Grid>
  </>
);

// Component for the page footer
const PageFooter = () => (
  <Paper
    elevation={0}
    sx={{
      position: "relative",
      padding: "0 5px",
    }}
  >
    <Grid container spacing={0.5}>
      <Grid item xs={3}>
        <Box sx={{ bgcolor: "#5b8dc0", height: "30px" }}></Box>
      </Grid>
      <Grid item xs={6}>
        <Box sx={{ bgcolor: "#2c4b73", height: "30px" }}></Box>
      </Grid>
      <Grid item xs={3}>
        <Box sx={{ bgcolor: "#5b8dc0", height: "30px" }}></Box>
      </Grid>
    </Grid>

    <Grid
      container
      spacing={0}
      sx={{ borderBottom: "4px solid #757070", py: 0 }}
    >
      <Grid item xs={3} sx={{ textAlign: "center" }}>
        <Typography fontSize={10}>WWW.ASQI.IN</Typography>
      </Grid>
      <Grid item xs={6} sx={{ textAlign: "center" }}>
        <Typography fontSize={10}>CONNECT@ASQI.IN</Typography>
      </Grid>
      <Grid item xs={3} sx={{ textAlign: "center" }}>
        <Typography fontSize={10}>+91 98208 93430</Typography>
      </Grid>
    </Grid>

    <Grid container spacing={0} sx={{ py: 1 }}>
      <Grid item xs={3} sx={{ textAlign: "center" }}>
        <Typography fontSize={10}>CIN NO - U62099MH2016FTC274805</Typography>
      </Grid>
      <Grid item xs={9} sx={{ textAlign: "center" }}>
        <Typography fontSize={10}>
          H 532, KANAKIA ZILLION, BKC ANNEXE, KURLA (WEST), MUMBAI 400 070.
        </Typography>
      </Grid>
    </Grid>
  </Paper>
);

// Main component
const BiocharOrderInvoicePage = () => {
  const { orderId, accessKey } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [invoiceData, setInvoiceData] = useState(null);

  useEffect(() => {
    if (!orderId) return;
    fetchInvoiceData(orderId);
  }, [orderId]);

  const fetchInvoiceData = async (orderId) => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${SERVER_BASE_ADDRESS}/biochar/get-biochar-order-invoice-details/${orderId}`,
        {
          headers: {
            Authorization: `Token ${accessKey}`,
          },
        }
      );
      setInvoiceData(response.data.data);
      setError(null);
    } catch (err) {
      console.error("Error fetching invoice data:", err);
      setError("Failed to fetch invoice details");
    } finally {
      setLoading(false);
    }
  };

  // Watermark component
  const Watermark = () => (
    <Box
      sx={{
        position: "absolute",
        top: "52%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        opacity: 0.1,
        zIndex: 0,
        width: "80%",
        height: "90%",
        backgroundImage: `url(${ASQILogoBlue})`,
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center",
        backgroundSize: "contain",
        color: "black",
      }}
    />
  );

  // Render invoice content
  const renderInvoiceContent = () => {
    if (error) {
      return <Typography color="error">{error}</Typography>;
    }

    if (!invoiceData) {
      return null;
    }

    // Debug log to help troubleshoot payment_mode
    console.log("Invoice Data:", invoiceData);
    console.log("Payment Mode:", invoiceData?.payment_mode);

    return (
      <>
        <Paper
          sx={{
            padding: "0 15mm",
            elevation: 0,
            position: "relative",
            borderRadius: 0,
            boxShadow: "none",
          }}
        >
          <Watermark />
          <InvoiceHeader invoiceData={invoiceData} />
          <InvoiceTable invoiceData={invoiceData} />
          <InvoiceFooterInfo invoiceData={invoiceData} />
        </Paper>
        <PageFooter />
      </>
    );
  };

  // Main component return
  return (
    <Box
      sx={{ maxWidth: 800, margin: "auto", backgroundColor: "#ffffff", mt: 1 }}
    >
      <Helmet>
        <title>Biochar Order Invoice - Farmapp</title>
      </Helmet>
      <LoaderContainer loading={loading}>
        {renderInvoiceContent()}
      </LoaderContainer>
    </Box>
  );
};

export default BiocharOrderInvoicePage;
